import { enumMembers, type ApiSchema } from "../apiSchema";
import { requestJson, withQuery } from "../http/httpClient";
import { ITEM_KINDS, type ItemKind } from "./reviewDecoders";
import { readTypeLineage, type TypeLineage } from "./typeLineage";
import { readTypeRef, type DataTypeRef } from "./workflowsApi";
import { JsonContractError, readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { readProjectedValue, type ProjectedValue } from "../threads/liveMessages";

/**
 * Module B's answer to "where does each step input's value come from" (ADR-0032 D4, D7;
 * Johannes, 27 Oct, point 8; built provisionally, awaiting acceptance): a step's plan, which
 * `prepare` gives before a start and a Thread's step plans give while it runs. The web app
 * renders these and decides none of them.
 */

export type ValueSource = ApiSchema<"ValueSource">;
export type DisabledCode = ApiSchema<"DisabledCode">;

export const VALUE_SOURCES = enumMembers<ValueSource>({
  run_value: true,
  wired_output: true,
  carried_output: true,
  context: true,
  workflow_default: true,
  step_default: true,
  project_item: true,
  wired_pending: true,
  missing: true,
});
export const DISABLED_CODES = enumMembers<DisabledCode>({
  missing_context: true,
  missing_input: true,
  invalid_value: true,
  unknown_input: true,
  does_not_resolve: true,
  not_a_value_input: true,
  abstract_workflow: true,
});
const STEP_KINDS = enumMembers<StepPlan["stepKind"]>({ agent: true, human: true, tool: true });
/** The sources whose value is an earlier step's output, which module C sums up for a person (WD-4). */
const MADE_SOURCES: ReadonlySet<ValueSource> = new Set<ValueSource>(["wired_output", "carried_output"]);

export interface DisabledReason {
  readonly code: DisabledCode;
  /** The input it concerns: a name, or a destination `<step key>.<input>`; `null` for the Workflow. */
  readonly input: string | null;
  /** The data types of the input it concerns. */
  readonly accepts: readonly DataTypeRef[];
}

export interface PlannedInput {
  readonly name: string;
  readonly label: string;
  readonly description: string;
  /** The data types it accepts: a value of any of them (ADR-0032 D1). */
  readonly accepts: readonly DataTypeRef[];
  readonly required: boolean;
  readonly source: ValueSource;
  /** The value from its source; `null` from an earlier step's output (`made`), the context, a pending wire, or when missing. */
  readonly value: unknown;
  /** What an earlier step made, at a glance (WD-4), for a `wired_output` or `carried_output` source; `null` for the others. */
  readonly made: ProjectedValue | null;
  /** The default that applies when nothing more specific fills it. */
  readonly default: unknown;
  /** The earlier step and part it is wired to. With a default source, that step ran and gave nothing. */
  readonly wiredFrom: { readonly stepKey: string; readonly part: string } | null;
  /** A run's value can fill it. */
  readonly takesValue: boolean;
  /** It takes one library item: a reference picked from the library fills it, and the server reads the item (F38). */
  readonly takesItem: boolean;
  /** Each accepted type's lineage, in the order of `accepts` (ADR-0032 D8): what a view is picked by. */
  readonly lineages: readonly TypeLineage[];
  /** With `takesItem`: the kinds whose versions may fill it; a project subtype is checked on the version when the step runs. */
  readonly itemKinds: readonly ItemKind[];
  /** With what has run and this run's values, a person gives it: required, a value fills it, and nothing else does. */
  readonly asksPerson: boolean;
  /** With what has run, a person picks the entry it reads: it takes a corpus entry, not a value, and the Thread works on none. */
  readonly asksEntry: boolean;
}

/** A required input of a step this one waits for, asked here: its value goes to `stepKey`. */
export interface AskedInput extends PlannedInput {
  readonly stepKey: string;
  /** `<step key>.<input>`: the key its value is sent under. */
  readonly destination: string;
}

/** The Thread binding that fills a context input: `subject` is the entries a Thread works on. */
export type ThreadBinding = NonNullable<ApiSchema<"PreparedInputBody">["binding"]>;

const THREAD_BINDINGS = enumMembers<ThreadBinding>({ subject: true, corpus: true, source_version: true });

/** A Workflow input a start fills; `destination` is the step input it goes to. */
export interface ExposedInput extends PlannedInput {
  readonly destination: string;
  /** A context input's binding, which the start binds rather than a value; `null` for an input a value fills (F99). */
  readonly binding: ThreadBinding | null;
}

/** A capability an agent step needs of its LLM (CAP-5): equal to a value, or at least a number. */
export type CapabilityRequirement = { readonly capability: string; readonly equals: boolean | number | string } | { readonly capability: string; readonly atLeast: number };

/**
 * What an agent step needs of the LLM it runs with (F106): a route of `routeType` that meets
 * `requirements`. Who carries it out, not an input; the Thread chooses the route.
 */
export interface LlmRequirements {
  readonly routeType: string;
  readonly requirements: readonly CapabilityRequirement[];
}

export interface StepPlan {
  readonly stepKey: string;
  readonly name: string;
  readonly stepKind: "agent" | "human" | "tool";
  /** It can run now: it waits for no step and lacks nothing. */
  readonly ready: boolean;
  /** The earlier steps that fill its pending inputs when they run, in order. */
  readonly waitsFor: readonly string[];
  /** Why it cannot run, apart from waiting. */
  readonly reasons: readonly DisabledReason[];
  readonly inputs: readonly PlannedInput[];
  /** What the steps it waits for lack, through the chain: asked here. */
  readonly asked: readonly AskedInput[];
  /** An agent step's prewritten prompt, where the server read it (F56). */
  readonly prompt: string | null;
  /** An agent step's LLM route slot; `null` for other steps. */
  readonly llmRequirements: LlmRequirements | null;
  /** By its placement alone: a person gives one of its inputs, or answers it (Johannes, 30 Oct). A run doesn't change it. */
  readonly needsPerson: boolean;
  /** An LLM carries out part of it; `null` while its tool doesn't resolve. */
  readonly usesLlm: boolean | null;
}

/** The Workflow prepared for one start: its exposed inputs, its steps' plans and what the start asks for. */
export interface PreparedWorkflow {
  readonly reference: string;
  readonly ready: boolean;
  readonly reasons: readonly DisabledReason[];
  /** The Workflow's exposed inputs, with their sources; a value for one is sent under its `name`. */
  readonly inputs: readonly ExposedInput[];
  readonly steps: readonly StepPlan[];
  /** Step inputs no exposed input fills that the start asks for, sent by destination. */
  readonly asked: readonly AskedInput[];
}

/**
 * Each step's plan of one Workflow version before anything ran, by flat step key, as module B plans a run's steps,
 * pins resolved at the project's content version (`commit`; the latest with `null`): what the graph shows each
 * placement's axes from (WD-1+3). Any version the project can read, an earlier one too, unlike `prepare`, which
 * starts only Workflows the project lists. Writes nothing.
 */
export function readWorkflowPlans(projectId: string, workflow: string, commit: string | null, signal?: AbortSignal): Promise<ReadonlyMap<string, StepPlan>> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/workflows/${encodeURIComponent(workflow)}/step-plans`, { version: commit }),
    signal,
    decodeResponse: (body) => {
      const steps = readArray(readObject(body, "Workflow step plans")["steps"], "step plans", readStepPlan);
      return new Map(steps.map((plan) => [plan.stepKey, plan]));
    },
  });
}

export function readPreparedWorkflow(object: JsonObject): PreparedWorkflow {
  return {
    reference: readString(object, "reference"),
    ready: readBoolean(object, "ready"),
    reasons: readArray(object["disabled_reasons"], "disabled reasons", readReason),
    inputs: readArray(object["inputs"], "prepared inputs", (item) => {
      const input = readObject(item, "prepared input");
      return {
        ...readPlannedInput(input),
        destination: readString(input, "destination"),
        binding: input["binding"] === undefined || input["binding"] === null ? null : readOneOf(input, "binding", THREAD_BINDINGS),
      };
    }),
    steps: readArray(object["steps"], "step plans", readStepPlan),
    asked: readArray(object["asked"], "asked inputs", readAskedInput),
  };
}

export function readStepPlan(value: unknown): StepPlan {
  const object = readObject(value, "step plan");
  return {
    stepKey: readString(object, "step_key"),
    name: readString(object, "name"),
    stepKind: readOneOf(object, "step_kind", STEP_KINDS),
    ready: readBoolean(object, "ready"),
    waitsFor: readArray(object["waits_for"], "waits_for", (item) => readText(item, "waits_for")),
    reasons: readArray(object["reasons"], "reasons", readReason),
    inputs: readArray(object["inputs"], "planned inputs", (item) => readPlannedInput(readObject(item, "planned input"))),
    asked: readArray(object["asked"], "asked inputs", readAskedInput),
    prompt: object["prompt"] === undefined ? null : readNullableString(object, "prompt"),
    llmRequirements: object["llm_requirements"] === undefined || object["llm_requirements"] === null ? null : readLlmRequirements(readObject(object["llm_requirements"], "llm_requirements")),
    needsPerson: readBoolean(object, "needs_person"),
    usesLlm: object["uses_llm"] === null ? null : readBoolean(object, "uses_llm"),
  };
}

function readLlmRequirements(object: JsonObject): LlmRequirements {
  return {
    routeType: readString(object, "route_type"),
    requirements: readArray(object["requirements"], "requirements", (item): CapabilityRequirement => {
      const requirement = readObject(item, "requirement");
      const capability = readString(requirement, "capability");
      if (requirement["at_least"] !== undefined) return { capability, atLeast: readInteger(requirement, "at_least") };
      const equals = requirement["equals"];
      if (typeof equals !== "boolean" && typeof equals !== "number" && typeof equals !== "string") throw new JsonContractError("Expected a requirement to have equals or at_least.");
      return { capability, equals };
    }),
  };
}

function readReason(value: unknown): DisabledReason {
  const object = readObject(value, "disabled reason");
  return {
    code: readOneOf(object, "code", DISABLED_CODES),
    input: readNullableString(object, "input"),
    accepts: readArray(object["accepts"], "accepted types", readTypeRef),
  };
}

/** A prepared Workflow input has no `wired_from`, `takes_value`, `asks_person` or `asks_entry`: it is filled at the start. */
function readPlannedInput(object: JsonObject): PlannedInput {
  const wire = object["wired_from"] === undefined || object["wired_from"] === null ? null : readObject(object["wired_from"], "wired_from");
  const source = readOneOf(object, "value_source", VALUE_SOURCES);
  // Module B carries an earlier step's output as module C summed it up (WD-4).
  const made = MADE_SOURCES.has(source) && object["value"] !== undefined && object["value"] !== null ? readProjectedValue(object["value"]) : null;
  return {
    name: readString(object, "name"),
    label: readString(object, "label"),
    description: readString(object, "description"),
    accepts: readArray(object["accepts"], "accepted types", readTypeRef),
    required: readBoolean(object, "required"),
    source,
    value: made === null ? (object["value"] ?? null) : null,
    made,
    default: object["default"] ?? null,
    wiredFrom: wire === null ? null : { stepKey: readString(wire, "step_key"), part: readString(wire, "part") },
    takesValue: object["takes_value"] === undefined ? true : readBoolean(object, "takes_value"),
    takesItem: object["takes_item"] === undefined ? false : readBoolean(object, "takes_item"),
    lineages: object["lineages"] === undefined ? [] : readArray(object["lineages"], "type lineages", readTypeLineage),
    itemKinds: object["item_kinds"] === undefined ? [] : readArray(object["item_kinds"], "item kinds", (kind) => readOneOf({ kind }, "kind", ITEM_KINDS)),
    asksPerson: object["asks_person"] === undefined ? false : readBoolean(object, "asks_person"),
    asksEntry: object["asks_entry"] === undefined ? false : readBoolean(object, "asks_entry"),
  };
}

function readAskedInput(value: unknown): AskedInput {
  const object = readObject(value, "asked input");
  return { ...readPlannedInput(object), stepKey: readString(object, "step_key"), destination: readString(object, "destination") };
}

function readText(value: unknown, context: string): string {
  if (typeof value !== "string") throw new JsonContractError(`Expected each of ${context} to be a string.`);
  return value;
}
